import {useMessage} from '@coolcolduk/ui-mui';
import {ContentCopy} from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {useCallback, useState} from 'react';
import {QuestionFormData} from '../../types/QuestionFormData';
import {useGenerateQuestionGraphJsonPrompts} from '../../hooks/generate/useGenerateQuestionGraphJsonPrompts';

export interface GraphJsonPromptDialogProps {
  open: boolean;
  formData: QuestionFormData;
  onClose: () => void;
}

export default function GraphJsonPromptDialog({open, formData, onClose}: GraphJsonPromptDialogProps) {
  const {showMessage} = useMessage();
  const {generateQuestionGraphJsonPrompts, loading} = useGenerateQuestionGraphJsonPrompts();
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');

  const handleGeneratePrompts = useCallback(async () => {
    if (!formData.questionText.trim()) {
      showMessage('Please fill in the question text', 6000, 'error');
      return;
    }

    try {
      const prompts = await generateQuestionGraphJsonPrompts({
        questionText: formData.questionText ?? '',
        topic: formData.topic || undefined,
        difficulty: formData.difficulty || undefined,
        hint: formData.hint || undefined,
        options: formData.options.length > 0 ? formData.options : undefined,
        answer: formData.answer || undefined,
        explanation: formData.explanation || undefined,
      });

      setSystemPrompt(prompts.system);
      setUserPrompt(prompts.user);
    } catch (error) {
      console.error('Error generating prompts:', error);
      showMessage('Failed to generate prompts. Please try again.', 6000, 'error');
    }
  }, [formData, generateQuestionGraphJsonPrompts, showMessage]);

  const handleCopySystemPrompt = useCallback(() => {
    navigator.clipboard.writeText(systemPrompt);
    showMessage('System prompt copied to clipboard', 3000, 'success');
  }, [systemPrompt, showMessage]);

  const handleCopyUserPrompt = useCallback(() => {
    navigator.clipboard.writeText(userPrompt);
    showMessage('User prompt copied to clipboard', 3000, 'success');
  }, [userPrompt, showMessage]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{fontWeight: 600, fontSize: '1.25rem'}}>Generate Graph JSON Prompts</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{pt: 2}}>
          <Button
            variant="contained"
            onClick={handleGeneratePrompts}
            disabled={!formData.questionText.trim() || loading}
            sx={{
              bgcolor: '#FF6F59',
              '&:hover': {bgcolor: '#FF6F59', opacity: 0.8},
            }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Generate Prompts'}
          </Button>

          {systemPrompt && (
            <Box>
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                  System Prompt
                </Typography>
                <Tooltip title="Copy system prompt">
                  <IconButton onClick={handleCopySystemPrompt} size="small">
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={12}
                value={systemPrompt}
                InputProps={{readOnly: true}}
                sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
              />
            </Box>
          )}

          {userPrompt && (
            <Box>
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                  User Prompt
                </Typography>
                <Tooltip title="Copy user prompt">
                  <IconButton onClick={handleCopyUserPrompt} size="small">
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={8}
                value={userPrompt}
                InputProps={{readOnly: true}}
                sx={{fontFamily: 'monospace', fontSize: '0.875rem'}}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
